import './TournamentList.less';

import clsx from 'clsx';
import React, { useContext, useEffect } from 'react';
import { dispatch, dispatchSync, useReframe } from 'nike-re-framejs';
import i18n from '@i18n';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import { HybridTourContent } from './TournamentContent';
import BettingInfo from './BettingInfo';
import FavoriteButton from './favorites/FavoriteButton';
import NikeOneContext from '../context/NikeOneContext';
import { FavoritesInfo } from '@/favorites/components/FavoritesInfo';
import BackToTop from '@/common/components/BackToTop';
import VhModuleSlider from '@/virtualgames/views/VhModuleSlider';
import { timeToHide } from '@/virtualgames/services/vh-service';
import TournamentsOnTickets from '@/nikeone/components/TournamentsOnTickets';
import { openTournament, toggleTournament } from '@/nikeone/handlers';
import { loggedIn$ } from '@/account/subs';
import { useTurboLivePush } from 'nike-ui/src/components/turboLive/push/overview/useTurboLivePush';
import { liveTournamentIds$$ } from '@/nikeone/subs-detail';
import StatsIcon from '@/nikeone/components/StatsIcon';
import { ArrowOrange } from 'nike-ui/src/components/Arrows/Arrows';
import { isProfileCombinableBetsEnabled, isProfileSportBracketEnabled } from 'nike-ui/src/profiles';
import { CombinableIconWithInfo } from 'nike-ui/src/components/turboLive/components/SportOverview';
import { SportBracketIcon } from 'nike-ui/src/components/sportbracket/SportBracketIcon';
import { navigate } from '@/routing';
import { NIKE1 } from '@/routing/routes';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { fireGAEvent } from '@/analytics';
import { CashinPosition } from 'nike-ui/src/components/turboLive/components/cashin/constants';
import CashinPositionContext from 'nike-ui/src/common/CashinPositionContext';

// ak je pocet tournamentov v sporte mensi ako EXPANDED_TOURNAMENTS tak sa vsetky rozbalia
const EXPANDED_TOURNAMENTS = 2;

export default function TournamentList({ slug }) {
    const { favoritesOnly } = useContext(NikeOneContext);
    const { derefSub, subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const liveTournamentIds = derefSub(liveTournamentIds$$('filteredBoxStore'));
    useTurboLivePush(liveTournamentIds);
    let split = derefSub(['nikeone.tournamentList.split', slug, favoritesOnly]);
    const count = split.favs.length + split.tops.length + split.others.length;

    function goToPrematch() {
        navigate(NIKE1);
        fireGAEvent(CURRENT_PAGE, 'Prechod do ponuky');
    }

    if (!count) {
        const key = favoritesOnly ? 'nikeone.prematch.empty.favorites' : 'content.empty';
        return (
            <EmptyState
                text={i18n(key)}
                buttonOnClick={goToPrematch}
                buttonClassName="light-button"
                buttonText={favoritesOnly ? i18n('go-to-prematch') : null}
            />
        );
    }
    const implicitExpand = count <= EXPANDED_TOURNAMENTS;
    const items = [];
    const sport = slug.length
        ? slug
              .substring(1)
              .split('/')[0]
              .replace('-', '')
        : '';
    if (split.favs.length) {
        if (!favoritesOnly) {
            items.push(<SplitLabel key="split-fav" text="leagues.favorite" />);
        }
        split.favs.forEach(t => {
            items.push(renderTournament(t, implicitExpand, sport));
        });
    }
    if (!favoritesOnly) {
        if (split.tops.length) {
            items.push(<SplitLabel key="split-top" text="leagues.top" />);
            split.tops.forEach(t => {
                items.push(renderTournament(t, implicitExpand, sport));
            });
        }
        if ((split.favs.length || split.tops.length) && split.others.length) {
            items.push(<SplitLabel key="split-others" text="leagues.other" />);
        }
        split.others.forEach(t => {
            items.push(renderTournament(t, implicitExpand, sport));
        });
    }

    return (
        <div className="nikeone-overview layout-offer-rw" data-sport={sport}>
            {loggedIn ? (
                <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.PREMATCH_OVERVIEW_ON_TICKETS }}>
                    <TournamentsOnTickets slug={slug} favs={!!split.favs.length} />
                </CashinPositionContext.Provider>
            ) : null}
            {favoritesOnly ? <FavoritesInfo /> : null}
            {items}
            <BettingInfo />
            {items.length < 3 && !timeToHide ? <VhModuleSlider /> : null}
            {items.length > 10 ? <BackToTop /> : null}
        </div>
    );
}

function renderTournament(tour, implicitExpand, sportName) {
    return (
        <Tournament
            key={tour.boxId + tour.itemId}
            tour={tour}
            sportId={tour.sportId}
            sportName={sportName}
            implicitExpand={implicitExpand}
            statisticsId={tour.statisticsId}
            otherClass="overflow-visible"
        />
    );
}

function SplitLabel({ text }) {
    return (
        <div className="other-leagues">
            <h3>{i18n(text)}</h3>
        </div>
    );
}

export function Tournament({ tour, boxStore = 'filteredBoxStore', implicitExpand, sportId, statisticsId, otherClass }) {
    const { label, boxId, tournamentNote, allowMarketCombinations, sportBracketId } = tour;
    const matchCount = tour.matchCount || tour.matchesCount;
    useEffect(() => {
        if (implicitExpand) {
            dispatch(openTournament(boxId, boxStore));
        }
    }, [boxId, boxStore, implicitExpand]);
    const derefSub = useReframe();
    let expanded = derefSub(['nikeone/filter/isTournamentExpanded', boxStore, boxId]);
    const favorited = derefSub(['favourite.ids.menuItems.isFavorite', boxId]);

    function tournamentOnClick() {
        // fireGAEvent(CURRENT_PAGE, `${label} ${expanded ? 'Close' : 'Open'}`, 'Markety');
        dispatchSync(toggleTournament(boxId, boxStore));
    }

    return (
        <div data-atid="accordion" data-tournament={label} className={clsx('accordion', 'p-rel', { active: expanded })}>
            <span className={`sport-br p-abs bg-${sportId}`} />
            <Preloader boxId={boxId} />
            <div
                className={clsx('accordion-head flex items-center justify-between', otherClass)}
                onClick={tournamentOnClick}
            >
                <div className="flex flex-col flex-1">
                    <div className="flex items-center">
                        <div
                            className={clsx('fs-14', 'c-black-80', 'text-extra-bold', 'f-condensed', {
                                ellipsis: !expanded
                            })}
                        >
                            {label}
                        </div>
                        {matchCount && !expanded && (
                            <div
                                className="p-rel fs-10 c-black-50 text-extra-bold tournament-list-count"
                                data-atid="match-count"
                            >
                                {matchCount}
                            </div>
                        )}
                        {isProfileCombinableBetsEnabled() && allowMarketCombinations && (
                            <CombinableIconWithInfo btnClasses="mx-8 fs-0 py-5 px-5" />
                        )}
                        {isProfileSportBracketEnabled() && sportBracketId && (
                            <SportBracketIcon sportBracketId={sportBracketId} />
                        )}
                    </div>
                    <div className={'tournament-note'}>{tournamentNote}</div>
                </div>

                <div className="flex-center">
                    <FavoriteButton active={favorited} onClick={() => dispatch(['favourite.item.toggle', boxId])} />
                    <StatsIcon
                        statsId={statisticsId}
                        isTournament
                        usePlaceholder
                        size="14"
                        btnClass="px-10 py-5 mr-10 fs-0"
                        iconClass="tournament-list-stats"
                    />
                    <ArrowOrange sizeX="16" sizeY="16" otherClassName={clsx({ 'arrow-up': expanded })} />
                </div>
            </div>
            {expanded !== true && expanded !== false ? null : (
                // TODO: move to CSS
                <div style={{ display: expanded ? 'block' : 'none' }}>
                    <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.PREMATCH_OVERVIEW }}>
                        <HybridTourContent boxStore={boxStore} boxId={boxId} expanded={expanded} />
                    </CashinPositionContext.Provider>
                </div>
            )}
        </div>
    );
}

export function Preloader({ boxId }) {
    const derefSub = useReframe();
    const loading = derefSub(['nikeone/loading/box/pending', boxId]);
    return loading ? <div className="mini-preloader" /> : null;
}
